import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import { useAuth } from '../../contexts/AuthContext';
import { siteInductionsService } from '../../services/siteInductionsService';
import InductionCard from './components/InductionCard';

const SiteInductionsWorkerView = () => {
  const { user } = useAuth();
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterSite, setFilterSite] = useState('');

  useEffect(() => {
    if (user?.id) loadAssignments();
  }, [user?.id]);

  const loadAssignments = async () => {
    try {
      setLoading(true);
      setError('');
      const result = await siteInductionsService?.getInductionsForWorker(user?.id);
      if (result?.error) throw result.error;

      const items = result?.data || [];
      const enriched = await Promise.all(
        items?.map(async (a) => {
          const statusResult = await siteInductionsService?.getWorkerCompletionStatus(
            a?.induction_id,
            user?.id
          );
          return {
            ...a,
            worker_user_id: user?.id,
            completed: !!statusResult?.data,
            completed_at: statusResult?.data?.completed_at || null,
          };
        })
      );
      setAssignments(enriched);
    } catch (err) {
      console.error('[SiteInductionsWorkerView] load inductions failed:', err);
      setError('Failed to load inductions. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCompleted = (inductionId) => {
    setAssignments((prev) =>
      prev?.map((a) =>
        a?.induction_id === inductionId
          ? { ...a, completed: true, completed_at: new Date()?.toISOString() }
          : a
      )
    );
  };

  const sites = [...new Map(
    assignments
      ?.filter((a) => a?.induction?.site)
      ?.map((a) => [a?.induction?.site?.id, a?.induction?.site])
  )?.values()];

  const filtered = assignments?.filter((a) => {
    const ind = a?.induction;
    const matchSearch =
      !searchQuery ||
      ind?.title?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
      ind?.site?.site_name?.toLowerCase()?.includes(searchQuery?.toLowerCase());
    const matchStatus =
      filterStatus === 'all' ||
      (filterStatus === 'pending' && !a?.completed) ||
      (filterStatus === 'completed' && a?.completed);
    const matchSite = !filterSite || ind?.site?.id === filterSite;
    return matchSearch && matchStatus && matchSite;
  });

  const pendingCount = assignments?.filter((a) => !a?.completed)?.length;
  const completedCount = assignments?.filter((a) => a?.completed)?.length;

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex items-center justify-center h-[calc(100vh-4rem)] pt-16">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent" />
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet><title>My Inductions - SiteProof</title></Helmet>
      <div className="min-h-screen bg-background">
        <Header />
        <main className="pt-16">
          <div className="max-w-2xl mx-auto px-4 py-6">

            {/* Page Header */}
            <div className="mb-5">
              <div className="flex items-center gap-3 mb-1">
                <div className="w-10 h-10 rounded-xl bg-accent/10 flex items-center justify-center">
                  <Icon name="GraduationCap" size={20} className="text-accent" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-foreground">My Inductions</h1>
                  <p className="text-sm text-muted-foreground">Site induction documents assigned to you</p>
                </div>
              </div>
            </div>

            {/* Status Summary */}
            {assignments?.length > 0 && (
              <div className="grid grid-cols-2 gap-3 mb-5">
                <div className="bg-card border border-amber-200 rounded-xl p-4">
                  <div className="flex items-center gap-2">
                    <Icon name="Clock" size={18} className="text-amber-600" />
                    <div>
                      <p className="text-xl font-bold text-foreground">{pendingCount}</p>
                      <p className="text-xs text-muted-foreground">Pending</p>
                    </div>
                  </div>
                </div>
                <div className="bg-card border border-success/30 rounded-xl p-4">
                  <div className="flex items-center gap-2">
                    <Icon name="CheckCircle" size={18} className="text-success" />
                    <div>
                      <p className="text-xl font-bold text-foreground">{completedCount}</p>
                      <p className="text-xs text-muted-foreground">Completed</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Pending attention banner */}
            {pendingCount > 0 && (
              <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 mb-5">
                <Icon name="AlertTriangle" size={18} className="text-amber-600 mt-0.5 shrink-0" />
                <p className="text-sm text-amber-800 font-medium">
                  You have {pendingCount} induction{pendingCount !== 1 ? 's' : ''} requiring completion.
                </p>
              </div>
            )}

            {/* Error */}
            {error && (
              <div className="flex items-center gap-2 bg-destructive/10 border border-destructive/20 text-destructive rounded-lg px-4 py-3 mb-4 text-sm">
                <Icon name="AlertCircle" size={16} />
                <span>{error}</span>
              </div>
            )}

            {/* Filters */}
            {assignments?.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-4">
                <div className="relative flex-1 min-w-[160px]">
                  <Icon name="Search" size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <input
                    type="text"
                    placeholder="Search inductions..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e?.target?.value)}
                    className="w-full pl-8 pr-3 py-2 text-sm border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent"
                  />
                </div>
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e?.target?.value)}
                  className="px-3 py-2 text-sm border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-accent/30"
                >
                  <option value="all">All</option>
                  <option value="pending">Pending</option>
                  <option value="completed">Completed</option>
                </select>
                {sites?.length > 1 && (
                  <select
                    value={filterSite}
                    onChange={(e) => setFilterSite(e?.target?.value)}
                    className="px-3 py-2 text-sm border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-accent/30"
                  >
                    <option value="">All Sites</option>
                    {sites?.map((s) => (
                      <option key={s?.id} value={s?.id}>{s?.site_name}</option>
                    ))}
                  </select>
                )}
              </div>
            )}

            {/* Induction Cards */}
            {filtered?.length === 0 ? (
              <div className="text-center py-12 bg-card border border-border rounded-xl">
                <Icon name="GraduationCap" size={36} className="text-muted-foreground mx-auto mb-3" />
                <p className="text-base font-medium text-foreground">
                  {assignments?.length === 0 ? 'No inductions assigned yet' : 'No inductions match your filters'}
                </p>
                <p className="text-sm text-muted-foreground mt-1">
                  {assignments?.length === 0
                    ? 'Your site manager will assign inductions when required' :'Try adjusting your search or filters'}
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Pending first */}
                {filtered?.filter((a) => !a?.completed)?.map((a) => (
                  <InductionCard
                    key={a?.id}
                    assignment={a}
                    onCompleted={handleCompleted}
                  />
                ))}
                {/* Then completed */}
                {filtered?.filter((a) => a?.completed)?.map((a) => (
                  <InductionCard
                    key={a?.id}
                    assignment={a}
                    onCompleted={handleCompleted}
                  />
                ))}
              </div>
            )}
          </div>
        </main>
      </div>
    </>
  );
};

export default SiteInductionsWorkerView;
